import { useState, useEffect, useMemo, useCallback } from 'react';
import { T, amountStyle } from '../theme';
import { loadFile } from '../utils/firestoreStorage';
import { formatAmount } from '../utils/formatters';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Info, ArrowLeft } from 'lucide-react';
import { FinanceViewNav, FinanceViewTopBar } from './NavShared';
import DateRangePicker, { getPresetRange } from './DateRangePicker';
import { categoryLabel, normalizeCategory } from '../utils/categoryHelpers';
import { UNCLASSIFIED_KEYS, buildTransferPairIds, classifyTransaction } from '../utils/autoClassify';
import { resolveClassification } from '../utils/constants';

function lsKey(uid) { return uid ? `financial_summary_settings_${uid}_v1` : 'financial_summary_settings_v1'; }
function loadSettings(uid) {
  try { return JSON.parse(localStorage.getItem(lsKey(uid))) ?? {}; }
  catch { return {}; }
}



// ─── Small components ─────────────────────────────────────────────────────────

function Delta({ current, previous, inverted = false }) {
  if (previous == null || previous === 0) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const up = pct >= 0;
  // For expenses, up is bad (inverted=true)
  const positive = inverted ? !up : up;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
      background: positive ? '#F0FDF4' : '#FEF2F2',
      color: positive ? '#15803d' : '#dc2626',
      marginLeft: 4,
    }}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function BarRow({ label, value, maxValue, color, currency }) {
  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      <div style={{ width: 120, fontSize: 11, color: '#374151', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 12, background: '#F3F4F6', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6, transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ width: 72, fontSize: 11, fontWeight: 700, color, textAlign: 'right', flexShrink: 0, ...amountStyle() }}>
        {formatAmount(value, currency)}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FinancialSummaryView({ uid, savedFiles, allLoaded, setAllLoaded, defaultCurrency, onGoToDashboard, onGoToBusiness, onGoToFinances, onGoToTax, onGoToExpenses, onGoToAdd, onGoToCapture, onImportCreditCard, onImportBank, topBarProps }) {
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => loadSettings(uid).dateFrom ?? getPresetRange('this_month').from);
  const [dateTo,   setDateTo]   = useState(() => loadSettings(uid).dateTo   ?? getPresetRange('this_month').to);

  useEffect(() => {
    localStorage.setItem(lsKey(uid), JSON.stringify({ dateFrom, dateTo }));
  }, [uid, dateFrom, dateTo]);

  // Load any files not yet in allLoaded
  const loadMissing = useCallback(async () => {
    if (!uid || !savedFiles.length) return;
    const missing = savedFiles.filter(f => !(f.name in allLoaded));
    if (!missing.length) return;
    setLoading(true);
    try {
      const updates = {};
      await Promise.all(missing.map(async (f) => {
        const file = await loadFile(uid, f.name, f.reportType);
        if (file) updates[f.name] = file.data;
      }));
      setAllLoaded(prev => ({ ...prev, ...updates }));
    } finally {
      setLoading(false);
    }
  }, [uid, savedFiles, allLoaded, setAllLoaded]);

  useEffect(() => { loadMissing(); }, [loadMissing]); // eslint-disable-line react-hooks/exhaustive-deps

  // All transactions across all loaded files
  const allTransactions = useMemo(() => {
    const txs = [];
    for (const [reportName, data] of Object.entries(allLoaded)) {
      const fileInfo = savedFiles.find(f => f.name === reportName);
      const rType = fileInfo?.reportType ?? 'expense';
      for (const tx of (data ?? [])) {
        txs.push({ ...tx, _reportName: reportName, _reportType: rType });
      }
    }
    return txs;
  }, [allLoaded, savedFiles]);

  // Expense-report items from all files — used for receipt matching
  const allExpenseReportItems = useMemo(
    () => savedFiles.filter(f => !f.reportType || f.reportType === 'expense').flatMap(f => f.data ?? []),
    [savedFiles]
  );

  const transferPairIds = useMemo(() => buildTransferPairIds(allTransactions), [allTransactions]);

  const classifiedTransactions = useMemo(() => allTransactions.map(tx =>
    classifyTransaction(tx, { transferIds: transferPairIds, allExpenseItems: allExpenseReportItems })
  ), [allTransactions, transferPairIds, allExpenseReportItems]);

  // Filter to a date range — empty string means no bound on that side
  const filterByRange = useCallback((from, to, txs = classifiedTransactions) => {
    if (!from && !to) return txs;
    return txs.filter(tx => {
      if (!tx.date) return false;
      if (from && tx.date < from) return false;
      if (to   && tx.date > to)   return false;
      return true;
    });
  }, [classifiedTransactions]);

  // Skip the expense-report side of explicitly linked expense↔CC pairs to avoid double-counting.
  // The CC/bank charge is the authoritative amount; the expense entry is omitted.
  const isLinkedExpenseSide = (tx) => tx.pairedWithId && tx._reportType === 'expense';

  const computePnl = (txs) => {
    let income = 0, expenses = 0, unclassified = 0;
    for (const tx of txs) {
      if (isLinkedExpenseSide(tx)) continue;
      const cls = resolveClassification(tx.businessClassification, tx.isRefund, tx.transactionDirection);
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (cls === 'business_income') income += amt;
      else if (cls === 'business_expense') expenses += amt;
      else if (cls === 'business_expense_refund') expenses -= amt;
      else if (UNCLASSIFIED_KEYS.has(cls)) unclassified++;
    }
    return { income, expenses, net: income - expenses, unclassified };
  };

  const periodTxs = useMemo(() => filterByRange(dateFrom, dateTo), [filterByRange, dateFrom, dateTo]);
  const pnl = useMemo(() => computePnl(periodTxs), [periodTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Previous period of same length for deltas
  const prevPnl = useMemo(() => {
    if (!dateFrom || !dateTo) return { income: 0, expenses: 0, net: 0, unclassified: 0 };
    const from = new Date(dateFrom), to = new Date(dateTo);
    if (isNaN(from) || isNaN(to)) return { income: 0, expenses: 0, net: 0, unclassified: 0 };
    const duration = to - from;
    const prevTo = new Date(from - 86400000);
    const prevFrom = new Date(prevTo - duration);
    const pf = prevFrom.toISOString().slice(0, 10);
    const pt = prevTo.toISOString().slice(0, 10);
    return computePnl(filterByRange(pf, pt));
  }, [filterByRange, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uncategorized: business_expense/refund or business_income with no category set
  const uncategorized = useMemo(() => {
    let expenseAmt = 0, expenseCount = 0, incomeAmt = 0, incomeCount = 0;
    for (const tx of periodTxs) {
      if (isLinkedExpenseSide(tx)) continue;
      const cls = resolveClassification(tx.businessClassification, tx.isRefund, tx.transactionDirection);
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if ((cls === 'business_expense' || cls === 'business_expense_refund') && !tx.category) { expenseAmt += amt; expenseCount++; }
      else if (cls === 'business_income' && !tx.category) { incomeAmt += amt; incomeCount++; }
    }
    return { expenseAmt, expenseCount, incomeAmt, incomeCount };
  }, [periodTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  const SHOW_TOP = 16;

  // Top expense categories — all entries kept so sum equals pnl.expenses (refunds net into their category)
  const expenseByCategory = useMemo(() => {
    const map = new Map();
    for (const tx of periodTxs) {
      if (isLinkedExpenseSide(tx)) continue;
      const cls = resolveClassification(tx.businessClassification, tx.isRefund, tx.transactionDirection);
      if (cls !== 'business_expense' && cls !== 'business_expense_refund') continue;
      const cat = normalizeCategory(tx.category) || 'uncategorized';
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      const signed = cls === 'business_expense_refund' ? -amt : amt;
      map.set(cat, (map.get(cat) ?? 0) + signed);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [periodTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Top income categories — all entries kept so sum equals pnl.income
  const incomeByCategory = useMemo(() => {
    const map = new Map();
    for (const tx of periodTxs) {
      if (isLinkedExpenseSide(tx)) continue;
      if (tx.businessClassification !== 'business_income') continue;
      const cat = normalizeCategory(tx.category) || 'uncategorized';
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      map.set(cat, (map.get(cat) ?? 0) + amt);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [periodTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  // 6-month trend — uses classifiedTransactions so classification is consistent
  const monthlyTrend = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString('en-US', { month: 'short' });
      const txs = classifiedTransactions.filter(tx => tx.date?.startsWith(ym));
      const { income, expenses } = computePnl(txs);
      months.push({ ym, label, income, expenses });
    }
    return months;
  }, [classifiedTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

  const trendMax = useMemo(() =>
    Math.max(...monthlyTrend.flatMap(m => [m.income, m.expenses]), 1),
  [monthlyTrend]);

  const maxExpCat = expenseByCategory[0]?.[1] ?? 1;
  const maxIncCat = incomeByCategory[0]?.[1] ?? 1;
  const expOtherAmt = expenseByCategory.slice(SHOW_TOP).reduce((s, [, v]) => s + v, 0);
  const incOtherAmt = incomeByCategory.slice(SHOW_TOP).reduce((s, [, v]) => s + v, 0);

  const hasData = Object.keys(allLoaded).length > 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: T.appBg, display: 'flex', flexDirection: 'column',
      fontFamily: T.fontBody,
    }}>
      {/* Top bar */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 12px', flexShrink: 0 }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <button
            onClick={onGoToDashboard}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            <ArrowLeft size={14} color="#fff" /> Dashboard
          </button>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>Financial Summary</div>
        </div>
        {/* <FinanceViewNav
          current="financial"
          onGoToDashboard={onGoToDashboard}
          onGoToFinances={onGoToFinances}
          onGoToTax={onGoToTax}
          onGoToExpenses={onGoToExpenses}
          onGoToAdd={onGoToAdd}
          onGoToCapture={onGoToCapture}
          // onImportCreditCard={onImportCreditCard}
          // onImportBank={onImportBank}
        /> */}
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        />
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '14px 14px 40px' }}>

        {loading && !hasData && (
          <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', fontSize: 13 }}>Loading…</div>
        )}

        {!loading && !hasData && (
          <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No data yet</div>
            <div style={{ fontSize: 13 }}>Import a bank or credit card statement to get started.</div>
          </div>
        )}

        {hasData && (
          <>
            {/* Uncategorized expenses + income */}
            {(uncategorized.expenseCount > 0 || uncategorized.incomeCount > 0) && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {uncategorized.expenseCount > 0 && (
                  <button
                    onClick={() => onGoToBusiness('business_expense')}
                    style={{ flex: 1, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <AlertTriangle size={12} color="#dc2626" />
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.4 }}>Uncategorized Exp.</span>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: '#dc2626', ...amountStyle(), lineHeight: 1.1 }}>
                      {formatAmount(uncategorized.expenseAmt, defaultCurrency)}
                    </div>
                    <div style={{ fontSize: 10, color: '#dc2626', marginTop: 3 }}>
                      {uncategorized.expenseCount} item{uncategorized.expenseCount !== 1 ? 's' : ''} · Categorize →
                    </div>
                  </button>
                )}
                {uncategorized.incomeCount > 0 && (
                  <button
                    onClick={() => onGoToBusiness('business_income')}
                    style={{ flex: 1, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <AlertTriangle size={12} color="#15803d" />
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.4 }}>Uncategorized Inc.</span>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: '#15803d', ...amountStyle(), lineHeight: 1.1 }}>
                      {formatAmount(uncategorized.incomeAmt, defaultCurrency)}
                    </div>
                    <div style={{ fontSize: 10, color: '#15803d', marginTop: 3 }}>
                      {uncategorized.incomeCount} item{uncategorized.incomeCount !== 1 ? 's' : ''} · Categorize →
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* P&L cards */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[
                { label: 'Biz Income',   value: pnl.income,   prev: prevPnl.income,   color: '#15803d', bg: '#F0FDF4', border: '#BBF7D0', Icon: TrendingUp,   inverted: false },
                { label: 'Biz Expenses', value: pnl.expenses, prev: prevPnl.expenses, color: '#dc2626', bg: '#FEF2F2', border: '#FECACA', Icon: TrendingDown, inverted: true  },
                {
                  label: 'Net', value: Math.abs(pnl.net), prev: null,
                  color: pnl.net >= 0 ? '#15803d' : '#dc2626',
                  bg: pnl.net >= 0 ? '#F0FDF4' : '#FEF2F2',
                  border: pnl.net >= 0 ? '#BBF7D0' : '#FECACA',
                  Icon: Minus, prefix: pnl.net < 0 ? '−' : '',
                },
              ].map(({ label, value, prev, color, bg, border, Icon, inverted, prefix = '' }) => (
                <div key={label} style={{ flex: 1, background: bg, borderRadius: 12, padding: '10px 12px', border: `1px solid ${border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <Icon size={12} color={color} />
                    <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 900, color, ...amountStyle(), lineHeight: 1.1 }}>
                    {prefix}{formatAmount(value, defaultCurrency)}
                  </div>
                  {prev != null && <Delta current={value} previous={prev} inverted={inverted} />}
                </div>
              ))}
            </div>

            {/* 6-month trend */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                6-Month Trend
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', height: 72 }}>
                {monthlyTrend.map((m, i) => (
                  <div key={m.ym} style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    borderRight: i < monthlyTrend.length - 1 ? '1px solid rgba(0,0,0,0.36)' : 'none',
                    paddingRight: i < monthlyTrend.length - 1 ? 2 : 0,
                    paddingLeft: i > 0 ? 2 : 0,
                  }}>
                    <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: 56 }}>
                      <div style={{ flex: 1, background: '#15803d', borderRadius: '2px 2px 0 0', minHeight: m.income > 0 ? 2 : 0, height: `${(m.income / trendMax) * 100}%`, transition: 'height 0.4s ease' }} />
                      <div style={{ flex: 1, background: '#dc2626', borderRadius: '2px 2px 0 0', minHeight: m.expenses > 0 ? 2 : 0, height: `${(m.expenses / trendMax) * 100}%`, transition: 'height 0.4s ease' }} />
                    </div>
                    <div style={{ fontSize: 9, color: '#9CA3AF', textAlign: 'center' }}>{m.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <span style={{ fontSize: 10, color: '#15803d', fontWeight: 700 }}>█ Income</span>
                <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 700 }}>█ Expenses</span>
              </div>
            </div>

            {/* Income breakdown */}
            {incomeByCategory.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                  Income by Type
                </div>
                {incomeByCategory.slice(0, SHOW_TOP).map(([cat, amt]) => (
                  <BarRow key={cat} label={categoryLabel(cat) || cat} value={amt} maxValue={maxIncCat} color="#15803d" currency={defaultCurrency} />
                ))}
                {incOtherAmt > 0 && (
                  <BarRow label={`Other (${incomeByCategory.length - SHOW_TOP} more)`} value={incOtherAmt} maxValue={maxIncCat} color="#9CA3AF" currency={defaultCurrency} />
                )}
              </div>
            )}

            {/* Expense breakdown */}
            {expenseByCategory.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                  Expenses by Category ({expenseByCategory.length})
                </div>

                {expenseByCategory.slice(0, SHOW_TOP).map(([cat, amt]) => (
                  <BarRow key={cat} label={categoryLabel(cat) || cat} value={amt} maxValue={maxExpCat} color="#dc2626" currency={defaultCurrency} />
                ))}
                {expOtherAmt > 0 && (
                  <BarRow label={`Other (${expenseByCategory.length - SHOW_TOP} more)`} value={expOtherAmt} maxValue={maxExpCat} color="#9CA3AF" currency={defaultCurrency} />
                )}
                <span style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Info size={11} color="#9CA3AF" />
                  <span>Note that all expenses are shown, even if they are not deductible. See Tax reports for deductible expenses.</span>
                </span>
              </div>
            )}

            {expenseByCategory.length === 0 && incomeByCategory.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#9CA3AF', fontSize: 13 }}>
                No classified transactions for this period. <button onClick={onGoToBusiness} style={{ background: 'none', border: 'none', color: T.tealDeep, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Classify in Business View →</button>
              </div>
            )}
            
          </>
        )}
      </div>

      {/* {onGoToCapture && (
        <button
          onClick={onGoToCapture}
          title="Scan Receipt"
          style={{
            // position: 'absolute', bottom: 24, right: 20, zIndex: 10,
            position: 'absolute', bottom: 24, right: 60, zIndex: 10,
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: T.gradTeal, color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}
        >
          <Camera size={24} />
        </button>
      )} */}
    </div>
  );
}
